import {
  deserify,
  getMediaBasePath,
  Loader,
  PaginationButton,
  ResourcePageContent,
  shouldIncludeCredentialsInRequest,
  useCommonServiceClientContext,
  useLoadingTimer,
  useSecondsTimer,
} from '@geneo2-web/shared-ui';
import { useAppSelector } from 'apps/student/src/reduxStore/reduxHooks';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getPalModeFromPathName,
  PalAssessmentWrapper,
  palModes,
} from '../components/PalAssessmentWrapper';
import { Box, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import {
  contentViewSubmitApi,
  endModuleSessionApi,
  fetchInternalResourceIdApi,
  getConceptMasteryApi,
  getRecommendationsContentItemsApi,
  type IConceptMasteryResponse,
} from '../api/learners';
import {
  HOME,
  PAL_KNOWLEDGE_CONCEPTS,
  PAL_VIEW_CONTENT,
} from 'apps/student/src/routeHandling/RoutesNomenclature';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { ResourceContent as ResourceInfo } from '@protos/content_management/content.common.apis_pb';
import {
  ResourceCategoryEnum,
  ResourceContent,
} from '@protos/content_management/content.db_pb';
import { handleKnowledgeConceptsNavigation } from '../KnowledgeConcepts/knowledgeConceptsNavigationUtil';
import { PalModeChangePopup } from '../components/PalModeChangePopup';
import { usePalBackNavigationHandlers } from '../hooks/usePalBackNavigationHandlers';
import { usePalModePopup } from '../hooks/usePalModePopup';
import { PalResourceHeading } from '../components/PalResourceHeading';
import PalLoader from '../components/PalLoader';
import { EndSessionButton } from '../PalQuestion';
import { useProjectAssessmentEnabled } from '../../../utils/customHooks';

const PalContent: React.FC = () => {
  const {
    external_module_id,
    external_session_id,
    external_content_id,
    external_concept_id,
  } = useParams();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<
    'loading' | 'completed' | 'error' | 'loading-submit' | 'error-submit'
  >('loading');
  const location = useLocation();
  const palMode = getPalModeFromPathName(location.pathname);
  const currentPalMode =
    palModes.find((mode) => mode.key === palMode) || palModes[0];
  const currentPalModeKey = currentPalMode.key;
  const navigate = useNavigate();
  const { ContentCommonAPIServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const { userInfo } = useAppSelector((state) => deserify(state.auth));
  const isProjectAssessmentEnabled = useProjectAssessmentEnabled();
  const [resourceInfo, setResourceInfo] = useState<ResourceInfo | undefined>();
  const [resourceContent, setResourceContent] = useState<
    ResourceContent | undefined
  >();
  const pageContents = (
    new ResourceContent(resourceContent)?.resourceContentData?.pageContent || []
  ).sort((a, b) => a.pageNumber - b.pageNumber);
  const [pageNumber, setPageNumber] = useState(0);
  const { showPalModePopup, onPalModePopupOpenChange } = usePalModePopup({
    currentPalModeKey,
    uniqueId: external_concept_id,
  });
  const timer = useSecondsTimer({
    initialTime: 0,
    isPaused: showPalModePopup,
  });
  const [isLoaderLoaded, setIsLoaderLoaded] = useState(false);

  useEffect(() => {
    if (!external_content_id) {
      getCurrentSessionContentId();
    } else {
      fetchContentData();
    }
  }, [external_content_id]);

  const backButtonClick = useCallback(
    async (isHome?: true, isEndSession?: boolean) => {
      if (isEndSession || isHome) {
        if (external_session_id) {
          await endModuleSessionApi({ session_id: external_session_id });
        }
      }
      if (isHome) {
        navigate(HOME, {
          replace: true,
        });
      } else {
        if (isEndSession) {
          navigate(`${PAL_KNOWLEDGE_CONCEPTS}/${external_module_id}`, {
            replace: true,
          });
          return;
        }
        let path = `${PAL_KNOWLEDGE_CONCEPTS}/${external_module_id}`;
        if (external_session_id) {
          path = path + '?sessionId=' + external_session_id;
        }
        navigate(path, {
          replace: true,
        });
      }
    },
    [external_module_id, external_session_id, navigate]
  );

  usePalBackNavigationHandlers(backButtonClick, [external_session_id]);

  const getCurrentSessionContentId = async () => {
    if (!external_session_id) {
      return;
    }
    try {
      setLoading('loading');
      const res = await getRecommendationsContentItemsApi(external_session_id, {
        count: 1,
      });
      const recommendations = res?.recommendations || [];
      const external_content_id = recommendations[0]?.content_id;
      const concept_id = res.concept_id;
      if (external_content_id) {
        navigate(
          PAL_VIEW_CONTENT +
            '/' +
            external_module_id +
            '/' +
            external_session_id +
            '/' +
            concept_id +
            '/' +
            external_content_id,
          {
            replace: true,
          }
        );
      } else {
        setLoading('error');
        return;
      }
      setLoading('completed');
    } catch (err) {
      setLoading('error');
    }
  };

  const fetchContentData = async () => {
    try {
      setLoading('loading');
      if (!external_content_id) {
        setLoading('error');
        return;
      }
      timer.reset();
      const resourceId = (await fetchInternalResourceIdApi(external_content_id))
        ?.resource_id;
      const res =
        await ContentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchResourceContent(
          {
            personId: userInfo?.studentProfileId,
            personType: ProfileRolesEnum.PROFILE_ROLE_STUDENT,
            resourceId: resourceId,
          }
        );
      if (res?.data) {
        const resourceInfo = new ResourceInfo(res.data);
        const gcpJsonURL = resourceInfo.gcpJsonUrl;
        const isUserDataBucket =
          resourceInfo?.resourceCategory ===
          ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES;
        const mediaUrl = getMediaBasePath(
          gcpJsonURL,
          isUserDataBucket ? 'userDataBucket' : 'resourceContentBucket'
        );
        const modifiedUrl = isUserDataBucket
          ? `${mediaUrl}${mediaUrl.includes('?') ? '&' : '?'}v=${Date.now()}`
          : mediaUrl;
        const options: RequestInit = {};
        if (shouldIncludeCredentialsInRequest(modifiedUrl)) {
          options.credentials = 'include';
        }
        const gcpResponse = await (await fetch(modifiedUrl, options)).json();
        const resContent = new ResourceContent().fromJson(gcpResponse, {
          ignoreUnknownFields: true,
        });
        setResourceContent(resContent);

        setResourceInfo(resourceInfo);
        timer.start();
        setLoading('completed');
      } else {
        setLoading('error');
      }
    } catch (err) {
      setLoading('error');
    }
  };

  if (
    loading === 'loading' ||
    loading === 'loading-submit' ||
    !isLoaderLoaded
  ) {
    const info = // discuss
      loading === 'loading'
        ? {
            heading: t('PAL_VC_LOADER_HEADING'),
            subHeading: t('PAL_VC_LOADER_SUBHEADING'),
            messages: [
              t('PAL_VC_LOADER_MSG_1'),
              t('PAL_VC_LOADER_MSG_2'),
              t('PAL_VC_LOADER_MSG_3'),
            ],
          }
        : {
            heading: t('PAL_VC_LOADER_SUBMIT_HEADING'),
            subHeading: t('PAL_VC_LOADER_SUBMIT_SUB'),
            messages: [t('PAL_VC_LOADER_MSG_2')],
          };
    return (
      <PalLoader
        contentLockKey={loading}
        emoji="🧠"
        heading={info.heading}
        subHeading={info.subHeading}
        messages={info.messages}
        isLoaderLoaded={isLoaderLoaded}
        setIsLoaderLoaded={setIsLoaderLoaded}
      />
      // <Box
      //   sx={{
      //     width: '100%',
      //     height: '100%',
      //     display: 'flex',
      //     alignItems: 'center',
      //     justifyContent: 'center',
      //     bgcolor: '#f8fafc',
      //     flexDirection: 'column',
      //     gap: 2,
      //   }}
      // >
      //   <Typography sx={{ fontSize: 48 }}>📚</Typography>
      //   <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1e293b' }}>
      //     Personalizing your next step
      //   </Typography>
      // </Box>
    );
  }

  if (loading === 'error' || loading === 'error-submit') {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f8fafc',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography sx={{ fontSize: 48 }}>❓</Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1e293b' }}>
          {loading === 'error'
            ? t('PAL_VC_ERROR_CONTENT')
            : loading === 'error-submit'
            ? t('PAL_VC_ERROR_NAVIGATION')
            : t('PAL_VC_ERROR_GENERIC')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <PalModeChangePopup
        open={showPalModePopup}
        setOpen={onPalModePopupOpenChange}
        currentPalModeKey={currentPalModeKey}
      />
      <PalAssessmentWrapper>
        <Box
          sx={{
            background: '#fff',
            borderRadius: '15px',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
            padding: '16px 24px',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <Paper
            sx={{
              display: 'flex',
              gap: 1.75,
              p: 2,
              borderRadius: 3,
              borderLeft: '4px solid #3b82f6',
              bgcolor: '#eff6ff',
              mb: '15px',
            }}
          >
            <Typography sx={{ fontSize: 20 }}>📊</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="elementSmallText"
                sx={{
                  fontWeight: 600,
                  color: '#3b82f6',
                  textTransform: 'uppercase',
                }}
              >
                {t('PAL_VC_BASED_ON_PERFORMANCE')}
              </Typography>
              <Typography
                variant="elementSmallText"
                sx={{ color: '#1e293b', mt: 0.5 }}
              >
                {t('PAL_VC_RESOURCES_HELP')}
              </Typography>
            </Box>
          </Paper>
          {/* <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              marginBottom: '15px',
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: '#1e293b', textAlign: 'center' }}
            >
              {t('PAL_VC_RECOMMENDED_FOR_YOU')}
            </Typography>
            <Typography
              variant="elementSmallText"
              sx={{ color: '#64748b', textAlign: 'center' }}
            >
              {t('PAL_VC_CURATED_RESOURCES')}
            </Typography>
          </Box> */}
          {!!resourceInfo && resourceContent && (
            <>
              <PalResourceHeading resourceInfo={resourceInfo} />
              <ResourcePageContent
                isProjectAssessmentEnabled={isProjectAssessmentEnabled}
                pageContents={pageContents}
                pageNumber={pageNumber}
                customStyle={{
                  paddingTop: '15px',
                }}
                handleAnswerSubmit={(contentId: string, answer: string[]) => {}}
                showSubmitButton={true}
                resource_raw_data={resourceInfo}
                // allowUserControls={false}
              />
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 'auto',
                  pt: '15px',
                }}
              >
                <PaginationButton
                  isPalButton={true}
                  currentPage={pageNumber + 1}
                  totalPages={pageContents.length}
                  disabledPrevious={pageNumber == 0}
                  onNextClick={async () => {
                    if (pageContents[pageNumber + 1]) {
                      setPageNumber(pageNumber + 1);
                    } else {
                      if (
                        !external_session_id ||
                        !external_content_id ||
                        !external_concept_id ||
                        !external_module_id
                      ) {
                        return;
                      }
                      try {
                        setLoading('loading-submit');
                        const res = await contentViewSubmitApi(
                          external_session_id,
                          {
                            concept_id: external_concept_id,
                            content_id: external_content_id,
                            time_spent_seconds: timer.value,
                          }
                        );
                        await handleKnowledgeConceptsNavigation({
                          sessionId: external_session_id,
                          moduleId: external_module_id,
                          navigateToKnowledgeGraph: true,
                          navigate,
                          skipDiagnositc: true,
                        });
                        // setLoading('completed');
                      } catch (err) {
                        console.error(err);
                        setLoading('error-submit');
                      }
                    }
                  }}
                  onPreviousClick={() => {
                    if (pageContents[pageNumber - 1]) {
                      setPageNumber(pageNumber - 1);
                    }
                  }}
                />
              </Box>
              {external_module_id && external_session_id && (
                <Box sx={{ mx: 'auto', mt: '15px' }}>
                  <EndSessionButton
                    moduleId={external_module_id}
                    sessionId={external_session_id}
                    onEndSession={() => {
                      backButtonClick(undefined, true);
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </PalAssessmentWrapper>
    </>
  );
};

export default PalContent;
